import { apiService } from './apiService';
import { Partner } from '../types';

class PartnerService {
  private cache: Partner[] | null = null;
  private cacheExpiry: number = 0;
  private readonly CACHE_DURATION = 4 * 60 * 60 * 1000; // 4 hours
  private readonly STORAGE_KEY = 'partners_cache';
  private readonly STORAGE_TIMESTAMP_KEY = 'partners_cache_timestamp';
  private pendingRequest: Promise<Partner[]> | null = null;

  // Clear cache
  private clearCache() {
    this.cache = null;
    this.cacheExpiry = 0;
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.STORAGE_TIMESTAMP_KEY);
  }

  // Check if cache is valid
  private isCacheValid(): boolean {
    return this.cache !== null && Date.now() < this.cacheExpiry;
  }

  // Convert database partner to frontend format
  private convertDbPartnerToFrontend(dbPartner: any): Partner {
    return {
      id: dbPartner.id,
      name: dbPartner.name,
      type: dbPartner.type,
      description: dbPartner.description,
      location: dbPartner.location,
      website: dbPartner.website || undefined,
      contactEmail: dbPartner.contact_email || dbPartner.contactEmail || undefined,
      contactPhone: dbPartner.contact_phone || dbPartner.contactPhone || undefined,
      impact: dbPartner.impact,
      imageUrl: dbPartner.image_url || dbPartner.imageUrl || undefined,
      logoUrl: dbPartner.logo_url || dbPartner.logoUrl || undefined,
      active: dbPartner.active !== false,
      featured: dbPartner.featured === true,
      sortOrder: dbPartner.sort_order || dbPartner.sortOrder || 0,
      partnershipStartDate: dbPartner.partnership_start_date || dbPartner.partnershipStartDate || undefined,
      partnershipEndDate: dbPartner.partnership_end_date || dbPartner.partnershipEndDate || undefined,
      createdAt: dbPartner.created_at || dbPartner.createdAt || new Date().toISOString(),
      updatedAt: dbPartner.updated_at || dbPartner.updatedAt || new Date().toISOString()
    };
  }

  // Convert frontend partner to database format
  private convertFrontendPartnerToDb(partner: Omit<Partner, 'id' | 'createdAt' | 'updatedAt'>): any {
    return {
      name: partner.name,
      type: partner.type,
      description: partner.description,
      location: partner.location,
      website: partner.website || null,
      contact_email: partner.contactEmail || null,
      contact_phone: partner.contactPhone || null,
      impact: partner.impact,
      image_url: partner.imageUrl || null,
      logo_url: partner.logoUrl || null,
      active: partner.active,
      featured: partner.featured,
      sort_order: partner.sortOrder,
      partnership_start_date: partner.partnershipStartDate || null,
      partnership_end_date: partner.partnershipEndDate || null
    };
  }

  // Load cache from localStorage
  private loadCacheFromStorage(): void {
    try {
      const cachedData = localStorage.getItem(this.STORAGE_KEY);
      const cachedTimestamp = localStorage.getItem(this.STORAGE_TIMESTAMP_KEY);

      if (cachedData && cachedTimestamp) {
        const timestamp = parseInt(cachedTimestamp, 10);
        const now = Date.now();

        if (now - timestamp < this.CACHE_DURATION) {
          this.cache = JSON.parse(cachedData);
          this.cacheExpiry = timestamp;
          // console.log('📦 Loaded partners from localStorage cache');
        }
      }
    } catch (error) {
      console.error('Error loading cache from localStorage:', error);
    }
  }

  // Save cache to localStorage
  private saveCacheToStorage(partners: Partner[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(partners));
      localStorage.setItem(this.STORAGE_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error('Error saving cache to localStorage:', error);
    }
  }

  constructor() {
    // Load from localStorage immediately for instant response
    this.loadCacheFromStorage();
    if (this.cache && this.cache.length > 0) {
      // console.log(`🚀 PartnerService initialized - loaded ${this.cache.length} partners from cache`);
    }
  }

  // Get all partners with enhanced caching
  async getAllPartners(forceRefresh: boolean = false): Promise<Partner[]> {
    // Return cached data immediately if valid
    if (!forceRefresh && this.isCacheValid()) {
      // Refresh in background if cache is older than 30 minutes
      const cacheAge = Date.now() - this.cacheExpiry;
      if (cacheAge > 30 * 60 * 1000) {
        this.fetchAllPartnersFromDatabase().catch(() => {
          // Silently fail - we have cache
        });
      }
      return [...this.cache!];
    }

    // If cache is empty, fetch from database
    if (!this.cache || this.cache.length === 0) {
      if (this.pendingRequest) {
        return await this.pendingRequest;
      }

      this.pendingRequest = this.fetchAllPartnersFromDatabase();
      try {
        const partners = await this.pendingRequest;
        this.pendingRequest = null;
        return partners;
      } catch (error) {
        this.pendingRequest = null;
        throw error;
      }
    }

    // Cache expired - fetch fresh data
    return await this.fetchAllPartnersFromDatabase();
  }

  private async fetchAllPartnersFromDatabase(): Promise<Partner[]> {
    try {
      const data = await apiService.request<any[]>('/partners');

      const partners = (data || []).map(dbPartner => this.convertDbPartnerToFrontend(dbPartner));

      // Update cache
      this.cache = partners;
      this.cacheExpiry = Date.now();
      this.saveCacheToStorage(partners);

      return partners;
    } catch (error) {
      console.error('API error fetching partners:', error);
      return this.cache || [];
    }
  }

  // Get active partners - uses cache
  async getActivePartners(forceRefresh: boolean = false): Promise<Partner[]> {
    // Use cached data if available, otherwise fetch
    const allPartners = await this.getAllPartners(forceRefresh);
    return allPartners.filter(partner => partner.active);
  }

  async getFeaturedPartners(): Promise<Partner[]> {
    try {
      const data = await apiService.request<any[]>('/partners/featured');
      return (data || []).map(dbPartner => this.convertDbPartnerToFrontend(dbPartner));
    } catch (error) {
      console.error('API error fetching featured partners:', error);
      return [];
    }
  }

  async getPartnersByType(type: string): Promise<Partner[]> {
    try {
      const data = await apiService.request<any[]>(`/partners/type/${type}`);
      return (data || []).map(dbPartner => this.convertDbPartnerToFrontend(dbPartner));
    } catch (error) {
      console.error('API error fetching partners by type:', error);
      return [];
    }
  }

  async getPartnerById(id: string): Promise<Partner | null> {
    // Check cache first
    if (this.cache && this.cache.length > 0) {
      const cached = this.cache.find(p => p.id === id);
      if (cached) {
        // Revalidate in background
        this.fetchAllPartnersFromDatabase().catch(() => { });
        return cached;
      }
    }

    try {
      const data = await apiService.request<any>(`/partners/${id}`);
      const partner = this.convertDbPartnerToFrontend(data);

      // Update cache
      if (this.cache) {
        const idx = this.cache.findIndex(p => p.id === id);
        if (idx !== -1) this.cache[idx] = partner;
        else this.cache.push(partner);
        this.saveCacheToStorage(this.cache);
      }

      return partner;
    } catch (error) {
      console.error('API error fetching partner by ID:', error);
      return null;
    }
  }

  async addPartner(partnerData: Omit<Partner, 'id' | 'createdAt' | 'updatedAt'>): Promise<Partner | null> {
    try {
      const dbPartner = this.convertFrontendPartnerToDb(partnerData);

      const data = await apiService.request<any>('/partners', {
        method: 'POST',
        body: JSON.stringify(dbPartner)
      });

      this.clearCache();
      return this.convertDbPartnerToFrontend(data);
    } catch (error) {
      console.error('API error adding partner:', error);
      return null;
    }
  }

  async updatePartner(id: string, partnerData: Partial<Omit<Partner, 'id' | 'createdAt' | 'updatedAt'>>): Promise<Partner | null> {
    try {
      const updateData: any = {};

      if (partnerData.name !== undefined) updateData.name = partnerData.name;
      if (partnerData.type !== undefined) updateData.type = partnerData.type;
      if (partnerData.description !== undefined) updateData.description = partnerData.description;
      if (partnerData.location !== undefined) updateData.location = partnerData.location;
      if (partnerData.website !== undefined) updateData.website = partnerData.website;
      if (partnerData.contactEmail !== undefined) updateData.contact_email = partnerData.contactEmail;
      if (partnerData.contactPhone !== undefined) updateData.contact_phone = partnerData.contactPhone;
      if (partnerData.impact !== undefined) updateData.impact = partnerData.impact;
      if (partnerData.imageUrl !== undefined) updateData.image_url = partnerData.imageUrl;
      if (partnerData.logoUrl !== undefined) updateData.logo_url = partnerData.logoUrl;
      if (partnerData.active !== undefined) updateData.active = partnerData.active;
      if (partnerData.featured !== undefined) updateData.featured = partnerData.featured;
      if (partnerData.sortOrder !== undefined) updateData.sort_order = partnerData.sortOrder;
      if (partnerData.partnershipStartDate !== undefined) updateData.partnership_start_date = partnerData.partnershipStartDate;
      if (partnerData.partnershipEndDate !== undefined) updateData.partnership_end_date = partnerData.partnershipEndDate;

      const data = await apiService.request<any>(`/partners/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });

      this.clearCache();
      return this.convertDbPartnerToFrontend(data);
    } catch (error) {
      console.error('API error updating partner:', error);
      return null;
    }
  }

  async deletePartner(id: string): Promise<boolean> {
    try {
      await apiService.request(`/partners/${id}`, { method: 'DELETE' });
      this.clearCache();
      return true;
    } catch (error) {
      console.error('API error deleting partner:', error);
      return false;
    }
  }

  async togglePartnerActive(id: string): Promise<boolean> {
    try {
      // First get current partner to know its state
      const partner = await this.getPartnerById(id);
      if (!partner) return false;

      // Then flip it and update
      await this.updatePartner(id, { active: !partner.active });
      return true;
    } catch (error) {
      console.error('API error toggling partner active status:', error);
      return false;
    }
  }

  async togglePartnerFeatured(id: string): Promise<boolean> {
    try {
      // First get current partner
      const partner = await this.getPartnerById(id);
      if (!partner) return false;

      // Update
      await this.updatePartner(id, { featured: !partner.featured });
      return true;
    } catch (error) {
      console.error('API error toggling partner featured status:', error);
      return false;
    }
  }

  async reorderPartners(updates: { id: string; sortOrder: number }[]): Promise<boolean> {
    try {
      await apiService.request('/partners/reorder', {
        method: 'POST',
        body: JSON.stringify(updates)
      });

      this.clearCache();
      return true;
    } catch (error) {
      console.error('API error reordering partners:', error);
      return false;
    }
  }

  async searchPartners(query: string): Promise<Partner[]> {
    // Local search using cache/active partners
    try {
      const activePartners = await this.getActivePartners();
      const lowerQuery = query.toLowerCase();

      return activePartners.filter(partner =>
        partner.name.toLowerCase().includes(lowerQuery) ||
        (partner.description && partner.description.toLowerCase().includes(lowerQuery)) ||
        (partner.location && partner.location.toLowerCase().includes(lowerQuery))
      );
    } catch (error) {
      console.error('API error searching partners:', error);
      return [];
    }
  }

  // Get partner statistics
  async getPartnerStats(): Promise<{ total: number; byType: Record<string, number>; active: number; featured: number }> {
    try {
      const allPartners = await this.getAllPartners();

      const stats = {
        total: allPartners.length,
        byType: {} as Record<string, number>,
        active: allPartners.filter(p => p.active).length,
        featured: allPartners.filter(p => p.featured).length
      };

      // Count by type
      allPartners.forEach(partner => {
        if (partner.type) {
          stats.byType[partner.type] = (stats.byType[partner.type] || 0) + 1;
        }
      });

      return stats;
    } catch (error) {
      console.error('API error fetching partner stats:', error);
      return { total: 0, byType: {}, active: 0, featured: 0 };
    }
  }
}

export const partnerService = new PartnerService();
